import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BulkActionPanel } from '../../../BulkActionPanel/BulkActionPanel';
import Dropdown from '../../../DropDown/Dropdown';
import Icon from '../../../Icon2/Icon';

import SpaceAssetsModalSearch from './SpaceAssetsModalSearch';
import SpaceAssetsModalSort, { getThumbnailSortDropdownOptions } from './SpaceAssetsModalSort';
import SpaceAssetsModalBreadcrumb from './SpaceAssetsModalBreadcrumb';
import SpaceAssetsModalFilter from './SpaceAssetsModalFilter';
import { GalleryView, ListView, ThumbnailView } from '../ViewComponent';

import { AssetData, AssetValidationFilter, CustomDropdownOption } from '../../utils/types';
import { pickEntryDataForSuggestion } from './helper';
import { deriveValidationFilterFromField } from '../ViewComponent/viewHelper';
import {
  AM_V2_ASSET_PICKER_VIEW_STORAGE_KEY,
  SPACE_ASSETS_SEARCH_TYPES,
  SPACE_ASSETS_VIEW_TYPE,
  SPACE_ASSETS_THUMBNAIL_SORT_KEYS,
  SPACE_ASSETS_SORT,
  SPACE_ASSETS_VIEW_ICON
} from '../../utils/constants';

import isEmpty from 'lodash/isEmpty';
import Popover from '../../../Popover/Popover';
import { Heading } from '../../../Typography/Heading/Heading';
import { Paragraph } from '../../../Typography/Paragraph/Paragraph';
import Radio from '../../../Radio/Radio';
import Button from '../../../Button/Button';
import TextInput from '../../../TextInput/TextInput';
import ToggleSwitch from '../../../ToggleSwitch/ToggleSwitch';
import Field from '../../../Field/Field';
import FieldLabel from '../../../FieldLabel/FieldLabel';
import Tooltip from '../../../Tooltip/Tooltip';
import InformationPill from '../common/InformationPill';
import Info from '../../../Info/Info';

const POPOVER_OFFSET_CONFIG = {
  large: { left: 420, top: 8 },
  medium: { left: 370, top: 0 },
  small: { left: 320, top: 0 },
  xsmall: { left: 270, top: 0 }
};

const getPopoverOffsets = (windowWidth: number) => {
  if (windowWidth >= 1245) {
    return POPOVER_OFFSET_CONFIG.large;
  } else if (windowWidth >= 992) {
    return POPOVER_OFFSET_CONFIG.medium;
  } else if (windowWidth >= 768) {
    return POPOVER_OFFSET_CONFIG.small;
  }
  return POPOVER_OFFSET_CONFIG.xsmall;
};

const useResponsivePopoverOffset = () => {
  const [offsets, setOffsets] = useState(() => getPopoverOffsets(window.innerWidth));

  useEffect(() => {
    const handleResize = () => {
      setOffsets(getPopoverOffsets(window.innerWidth));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return offsets;
};

const SuggestionsAiWarningLabel = ({ message }: { message?: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={anchorRef}
      className="SpaceAssetsModalSearch__ai-label SpaceAssetsModalSearch__ai-label--with-warning flex-v-center"
      data-test-id="suggestions-ai-warning-label"
      onClick={(e) => {
        e.stopPropagation();
        setIsOpen(true);
      }}
    >
      <span className="SpaceAssetsModalSearch__ai-label-main flex-v-center">
        <Icon icon="AIAssist" size="small" version="v2" />
        <span className="SpaceAssetsViewSelector__label-text">Suggestions</span>
      </span>
      <span className="SpaceAssetsModalSearch__ai-warning-icon" data-test-id="suggestions-ai-warning-icon">
        <Icon icon="Warning" version="v2" size="small" />
      </span>
      <Popover
        testId="suggestions-ai-warning-popover"
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        anchorRef={anchorRef}
        dismissOnClickOutside
        hasHeader
        header={
          <div className="SuggestionsAiWarning__header">
            <Icon icon="Warning" version="v2" size="small" />
            <Heading tagName="h6" text="Suggestions Unavailable" className="SuggestionsAiWarning__header-title" />
          </div>
        }
        hasFooter
        footer={
          <div className="SuggestionsAiWarning__footer">
            <Button
              buttonType="tertiary"
              size="small"
              onClick={(e?: React.MouseEvent) => { e?.stopPropagation(); setIsOpen(false); }}
            >
              Close
            </Button>
            <Button
              buttonType="tertiary"
              size="small"
              onClick={(e?: React.MouseEvent) => { e?.stopPropagation(); setIsOpen(false); }}
            >
              Contact Admin
            </Button>
          </div>
        }
      >
        <Paragraph tagName="p" variant="p2" className="SuggestionsAiWarning__message" text={message} />
      </Popover>
    </div>
  );
};

const buildSuggestionsLabel = (aiWarning: boolean, suggestionsAiWarningMessage?: string) =>
  aiWarning ? (
    <SuggestionsAiWarningLabel message={suggestionsAiWarningMessage} />
  ) : (
    <div className="SpaceAssetsModalSearch__ai-label flex-v-center">
      <Icon icon="AIAssist" size="small" version="v2" />
      <span className="SpaceAssetsViewSelector__label-text">Suggestions</span>
    </div>
  );

const SEARCH_DROPDOWN_LIST_ITEMS: {
  label: any | React.ReactNode;
  testId: string;
  value: string | React.ReactNode;
  disable: boolean;
  [key: string]: any;
}[] = [
    {
      label: (
        <div className="SpaceAssetsModalSearch__ai-label flex-v-center">
          <Icon icon="AIAssist" size="small" version="v2" />
          <span className="SpaceAssetsViewSelector__label-text">Suggestions</span>
        </div>
      ),
      testId: 'am-v2-space-assets-search-in-suggested-assets',
      value: (
        <div className="SpaceAssetsModalSearch__ai-label flex-v-center">
          <Icon icon="AIAssist" size="small" version="v2" />
          <span className="SpaceAssetsViewSelector__label-text">Suggestions</span>
        </div>
      ),
      disable: false
    },
    {
      label: 'All (Search within all fields)',
      testId: 'am-v2-space-assets-search-in-all',
      value: SPACE_ASSETS_SEARCH_TYPES.ALL,
      disable: false
    },
    {
      label: 'Title (Search within title only)',
      testId: 'am-v2-space-assets-search-in-title',
      value: SPACE_ASSETS_SEARCH_TYPES.TITLE,
      disable: false
    },
    {
      label: 'URL (Search within url only)',
      testId: 'am-v2-space-assets-search-in-url',
      value: SPACE_ASSETS_SEARCH_TYPES.URL,
      disable: false
    }
  ];

const SPACE_ASSETS_VIEW_SELECTOR_OPTIONS = [
  {
    label: (
      <div className="SpaceAssetsViewSelector__label flex-v-center">
        <Icon icon="Thumbnail" size="small" version="v2" />
        <span className="SpaceAssetsViewSelector__label-text">Thumbnail</span>
      </div>
    ),
    value: SPACE_ASSETS_VIEW_TYPE.THUMBNAIL,
    disable: false
  },
  {
    label: (
      <div className="SpaceAssetsViewSelector__label flex-v-center">
        <Icon icon="ListDashes" size="small" version="v2" />
        <span className="SpaceAssetsViewSelector__label-text">List</span>
      </div>
    ),
    value: SPACE_ASSETS_VIEW_TYPE.LIST,
    disable: false
  },
  {
    label: (
      <div className="SpaceAssetsViewSelector__label flex-v-center">
        <Icon icon="Gallery" size="medium" version="v2" />
        <span className="SpaceAssetsViewSelector__label-text">Gallery</span>
      </div>
    ),
    value: SPACE_ASSETS_VIEW_TYPE.GALLERY,
    disable: false
  }
];

const getIconNameForViewType = (type: string): string => SPACE_ASSETS_VIEW_ICON[type.toUpperCase() as keyof typeof SPACE_ASSETS_VIEW_ICON] ?? SPACE_ASSETS_VIEW_ICON.THUMBNAIL;

const getPersistedViewFromStorage = (storageKey: string, persist: boolean): string => {
  if (!persist || typeof window === 'undefined') {
    return SPACE_ASSETS_VIEW_TYPE.THUMBNAIL;
  }

  try {
    const stored = window.localStorage.getItem(storageKey);
    if (
      stored &&
      (Object.values(SPACE_ASSETS_VIEW_TYPE) as string[]).includes(stored)
    ) {
      return stored;
    }
  } catch (error) {
    console.error('Error getting persisted view from storage:', error);
  }

  return SPACE_ASSETS_VIEW_TYPE.THUMBNAIL;
};

const getViewSelectorDropdownOptions = (
  viewType: string,
  handleViewTypeChange: (view: string) => void
): CustomDropdownOption[] => {
  return SPACE_ASSETS_VIEW_SELECTOR_OPTIONS.map((option) => ({
    key: option.value,
    label: option.label,
    value: option.value,
    action: () => handleViewTypeChange(option.value),
    default: option.value === viewType,
    testId: `am-v2-space-assets-view-selector-dropdown-${option.value}-option`,
    className: `am-v2-space-assets-view-selector-dropdown-option`,
    disable: 'disable' in option ? option.disable : false
  }));
};

const SpaceAssetsSelectorModalBody = (props) => {
  const {
    selectedFolder,
    setSelectedFolder,
    breadcrumbPath,
    setBreadcrumbPath,
    breadcrumbClickHandler,
    setMessageComponent,
    setTotalAssestsSelectedValue,
    field = {},
    isSuggestions,
    setIsSuggestions,
    shouldClearSearchFilters,
    setShouldClearSearchFilters,
    handleGoBack,
    excludeUids,
    setShouldShowInfoComponent,
    spaceAssetsPicked,
    setSpaceAssetsPicked,
    componentResponsiveness,
    searchTypes,
    spacesList,
    linkedWorkspaces,
    spacesWithTitles,
    spaceAssetsSearchOptions,
    setSpaceAssetsSearchOptions,
    fetchSpaceAssets,
    isVariantPlanEnabled,
    isJsonRTE,
    isInline,
    setIsInline,
    embedAsLink,
    linkSettings,
    setLinkSettings,
    mode,
    getUserDisplayName,
    persistAssetPickerView = true,
    onSuggestionsFetchSettled,
    isNewlyCreatedFolder = false,
    infoBanner,
    aiEnabled,
    aiWarning = false,
    suggestionsAiWarningMessage,
  } = props;
  const isMoveTarget = mode === 'moveTarget';
  const isUploadTarget = mode === 'uploadTarget';
  const isFolderPickerMode = isMoveTarget || isUploadTarget;
  const { isResponsive = false, screenVariant = '' } = componentResponsiveness || {};
  const modalBodyRef = React.useRef<HTMLDivElement>(null);
  const [totalCounts, setTotalCounts] = useState(0);
  const [viewIcon, setViewIcon] = useState<string>(SPACE_ASSETS_VIEW_ICON.THUMBNAIL);
  const persistedViewType = useMemo(
    () => getPersistedViewFromStorage(AM_V2_ASSET_PICKER_VIEW_STORAGE_KEY, persistAssetPickerView),
    [persistAssetPickerView]
  );
  const [viewType, setViewType] = useState<string>(persistedViewType);
  const effectiveViewType = viewType;
  const [appliedFilterCount, setAppliedFilterCount] = React.useState(0);
  const [sortByData, setSortByData] = useState({
    id: SPACE_ASSETS_THUMBNAIL_SORT_KEYS.TITLE,
    sortingDirection: SPACE_ASSETS_SORT.ASC
  });
  const [isAPIError, setIsAPIError] = useState(false);
  const [shouldDisabledActionPanel, setShouldDisabledActionPanel] = useState(false);
  const initialSearchDropDownList =
    aiEnabled === false
      ? SEARCH_DROPDOWN_LIST_ITEMS.filter(
          (item) => item.testId !== 'am-v2-space-assets-search-in-suggested-assets'
        )
      : SEARCH_DROPDOWN_LIST_ITEMS.map((item) =>
          aiWarning && item.testId === 'am-v2-space-assets-search-in-suggested-assets'
            ? { ...item, disable: true, label: buildSuggestionsLabel(aiWarning, suggestionsAiWarningMessage) }
            : item
        );
  const [searchDropDownList, setSearchDropDownList] = useState(initialSearchDropDownList);
  const [showSelectedAssets, setShowSelectedAssets] = useState(false);
  const [isInfoBannerVisible, setIsInfoBannerVisible] = useState(true);
  const [localeInfoBannerDismissed, setLocaleInfoBannerDismissed] = useState(false);
  const [modalBulkActionPanelWidth, setModalBulkActionPanelWidth] = useState(900);
  const shouldShowInfoBanner = isUploadTarget && !!infoBanner && isInfoBannerVisible;
  const ai_weighted_query = pickEntryDataForSuggestion(props?.entryData, isVariantPlanEnabled());
  const [ai_suggestions, setAiSuggestions] = useState({
    space_uids: !isEmpty(selectedFolder) ? [(selectedFolder as AssetData)?.space_uid] : linkedWorkspaces?.map((ws) => ws.space_uid),
    ai_weighted_query: ai_weighted_query
  });

  const validationFilter: AssetValidationFilter | null = useMemo(() => {
    return deriveValidationFilterFromField(field);
  }, [field]);

  const [isEmbeddingPopoverOpen, setIsEmbeddingPopoverOpen] = useState(false);
  const [isLinkSettingsPopoverOpen, setIsLinkSettingsPopoverOpen] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ left: 0, top: 0 });
  const embeddingPopoverAnchorRef = useRef<HTMLElement>(null);
  const linkSettingsPopoverAnchorRef = useRef<HTMLElement>(null);

  const { left: POPOVER_OFFSET_LEFT, top: POPOVER_OFFSET_TOP } = useResponsivePopoverOffset();

  useEffect(() => {
    setAiSuggestions((prev) => ({
      ...prev,
      space_uids: !isEmpty(selectedFolder) ? [(selectedFolder as AssetData)?.space_uid] : linkedWorkspaces?.map((ws) => ws.space_uid)
    }));
  }, [linkedWorkspaces])

  const calculatePopoverPosition = (anchorRef?: React.RefObject<HTMLElement>) => {
    const ref = anchorRef || embeddingPopoverAnchorRef;
    if (ref.current) {
      const anchorRect = ref.current.getBoundingClientRect();
      return {
        left: anchorRect.left + window.scrollX + POPOVER_OFFSET_LEFT,
        top: anchorRect.bottom + window.scrollY + POPOVER_OFFSET_TOP
      };
    }
    return { left: 0, top: 0 };
  };

  useEffect(() => {
    document.documentElement.style.setProperty('--popover-left', `${popoverPosition.left}px`);
    document.documentElement.style.setProperty('--popover-top', `${popoverPosition.top}px`);
  }, [popoverPosition]);

  useEffect(() => {
    if (spaceAssetsPicked.length === 0 && isEmbeddingPopoverOpen) {
      setIsEmbeddingPopoverOpen(false);
    }
    if (spaceAssetsPicked.length === 0 && isLinkSettingsPopoverOpen) {
      setIsLinkSettingsPopoverOpen(false);
    }
  }, [spaceAssetsPicked.length, isEmbeddingPopoverOpen, isLinkSettingsPopoverOpen]);

  useEffect(() => {
    const isPopoverOpen = isEmbeddingPopoverOpen || isLinkSettingsPopoverOpen;
    const activeAnchorRef = isEmbeddingPopoverOpen ? embeddingPopoverAnchorRef : linkSettingsPopoverAnchorRef;

    if (!isPopoverOpen || !activeAnchorRef.current) return;

    let animationFrameId: number;
    let lastTop = 0;
    let lastLeft = 0;

    const updatePopoverPosition = () => {
      if (activeAnchorRef.current) {
        const anchorRect = activeAnchorRef.current.getBoundingClientRect();
        
        if (anchorRect.top !== lastTop || anchorRect.left !== lastLeft) {
          lastTop = anchorRect.top;
          lastLeft = anchorRect.left;
          setPopoverPosition(calculatePopoverPosition(activeAnchorRef));
        }
      }
      animationFrameId = requestAnimationFrame(updatePopoverPosition);
    };

    animationFrameId = requestAnimationFrame(updatePopoverPosition);

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [isEmbeddingPopoverOpen, isLinkSettingsPopoverOpen, calculatePopoverPosition]);

  useEffect(() => {
    setShouldShowInfoComponent(isAPIError || shouldDisabledActionPanel ? false : true);
  }, [isAPIError, shouldDisabledActionPanel]);

  const handleViewTypeChange = useCallback(
    (type: string) => {
      setViewType(type);
      if (persistAssetPickerView && typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(AM_V2_ASSET_PICKER_VIEW_STORAGE_KEY, type);
        } catch(error) {
          console.error('Error setting persisted view in storage:', error);
        }
      }
    },
    [persistAssetPickerView]
  );

  const getViewComponent = () => {
    switch (effectiveViewType) {
      case SPACE_ASSETS_VIEW_TYPE.THUMBNAIL:
        return ThumbnailView;
      case SPACE_ASSETS_VIEW_TYPE.LIST:
        return ListView;
      case SPACE_ASSETS_VIEW_TYPE.GALLERY:
        return GalleryView;
      default:
        return ThumbnailView;
    }
  };

  const RenderViewComponent = getViewComponent();

  const sortOptionsList = useMemo(
    () => getThumbnailSortDropdownOptions(sortByData.id, sortByData.sortingDirection, setSortByData, isResponsive),
    [isResponsive, sortByData]
  );

  const moveTargetViewSelectorOptions = SPACE_ASSETS_VIEW_SELECTOR_OPTIONS.filter(
    opt => opt.value !== SPACE_ASSETS_VIEW_TYPE.GALLERY
  );

  useEffect(() => {
    if (isFolderPickerMode) {
      setViewType(SPACE_ASSETS_VIEW_TYPE.THUMBNAIL);
    }
  }, [isFolderPickerMode]);

  const getMoveTargetViewSelectorDropdownOptions = (
    currentViewType: string,
    onViewTypeChange: (view: string) => void
  ): CustomDropdownOption[] => {
    return moveTargetViewSelectorOptions.map((option) => ({
      key: option.value,
      label: option.label,
      value: option.value,
      action: () => onViewTypeChange(option.value),
      default: option.value === currentViewType,
      testId: `am-v2-space-assets-view-selector-dropdown-${option.value}-option`,
      className: `am-v2-space-assets-view-selector-dropdown-option`,
      disable: 'disable' in option ? option.disable : false
    }));
  };

  const viewOptionList = useMemo(
    () => isFolderPickerMode
      ? getMoveTargetViewSelectorDropdownOptions(viewType, handleViewTypeChange)
      : getViewSelectorDropdownOptions(viewType, handleViewTypeChange),
    [viewType, isFolderPickerMode]
  );

  useEffect(() => {
    // bulkActionPanel wrapper positioning
    const bulkActionWrapper = document.querySelector('.bulk-action-wrapper') as HTMLElement;
    if (bulkActionWrapper) {
      const hasSelectedAssets = spaceAssetsPicked.length > 0;
      const styles = hasSelectedAssets
        ? { zIndex: '1000', top: isResponsive ? '6%' : '12%' }
        : { zIndex: '100', top: '0' };

      Object.assign(bulkActionWrapper.style, styles);
    }

    const bulkActionPanel = document.querySelector('.TableActionPanel') as HTMLElement;
    if (bulkActionPanel) {
      // Handling bulkActionPanel styles for responsive & non-responsive mode
      Object.assign(bulkActionPanel.style, {
        height: isResponsive ? '40px' : '56px'
      });

      // bulkActionPanel have two elements "bulk-panel-action-item-0" & "bulk-panel-action-item-1"
      const bulkActionItems = bulkActionPanel.querySelectorAll(
        '[id^="bulk-panel-action-item-"]'
      ) as NodeListOf<HTMLElement>;

      bulkActionItems.forEach((item, index) => {
        item.style.fontSize = isResponsive ? '12px' : 'initial';

        // Only apply eye icon styles to the first item
        if (index === 0) {
          const eyeIcon = item.querySelector('svg[name="Eye"]') as HTMLElement;
          if (eyeIcon) {
            Object.assign(eyeIcon.style, {
              width: isResponsive ? '20px' : '24px',
              height: isResponsive ? '20px' : '24px'
            });
          }
        }
      });
    }
  }, [spaceAssetsPicked, isResponsive]);

  const adjustBannerPosition = useCallback(() => {
    const banner = document.querySelector('.SpaceAssetsModalBody__locale-info-banner') as HTMLElement;
    const filterContent = document.querySelector('.SpaceAssetsModalFilter__content') as HTMLElement;
    
    if (banner && filterContent) {
      const filterHeight = filterContent.offsetHeight;
      
      let bannerMarginTop = '0px';
      
      const responsiveModifier = isResponsive ? 0.5 : 1;
            
      if (filterHeight <= 70) {
        bannerMarginTop = `-${14 * responsiveModifier}px`;
      } 
      else if(filterHeight > 70 && filterHeight <= 140) {
        bannerMarginTop = `${26 * responsiveModifier}px`;
      }
      else {
        bannerMarginTop = `${65 * responsiveModifier}px`;
      }
      
      banner.style.setProperty('margin-top', bannerMarginTop, 'important');
    }
  }, [isResponsive, effectiveViewType]);

  useEffect(() => {
    const updateWidth = () => {
      setModalBulkActionPanelWidth((modalBodyRef.current?.offsetWidth || 950) + 25);
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  useEffect(() => {
    const handleBannerAdjustment = () => {
      adjustBannerPosition();
    };

    window.addEventListener('adjustBannerPosition', handleBannerAdjustment);
    
    adjustBannerPosition();

    return () => {
      window.removeEventListener('adjustBannerPosition', handleBannerAdjustment);
    };
  }, [adjustBannerPosition, localeInfoBannerDismissed]);

  useEffect(() => {
    setSearchDropDownList((prevList) => {
      const isShowSelectedExists = prevList[4]?.value === SPACE_ASSETS_SEARCH_TYPES.SHOW_SELECTED;
      if (!isShowSelectedExists && showSelectedAssets) {
        return [
          ...prevList,
          {
            label: SPACE_ASSETS_SEARCH_TYPES.SHOW_SELECTED,
            testId: 'am-v2-space-assets-search-show-only-selected',
            value: SPACE_ASSETS_SEARCH_TYPES.SHOW_SELECTED,
            disable: false
          }
        ];
      }
      return initialSearchDropDownList;
    });
  }, [showSelectedAssets, setSearchDropDownList]);

  const handleBulkActionsCb = (value: string) => {
    if (value === 'show_all_or_selected') {
      setShowSelectedAssets((prev) => {
        setSpaceAssetsSearchOptions({
          searchType: !prev ? SPACE_ASSETS_SEARCH_TYPES.SHOW_SELECTED : SPACE_ASSETS_SEARCH_TYPES.ALL,
          searchText: ''
        });
        return !prev;
      });
    }

    if(value === 'apply_embedding_settings') {
        const buttonElement = document.querySelector('.bulk-action-items') as HTMLElement;
        if(buttonElement) {
            embeddingPopoverAnchorRef.current = buttonElement;
            setPopoverPosition(calculatePopoverPosition());
        }
        setIsEmbeddingPopoverOpen(true);
    }

    if(value === "apply_link_settings") {
        const buttonElement = document.querySelector(".bulk-action-items") as HTMLElement;
        if(buttonElement) {
            linkSettingsPopoverAnchorRef.current = buttonElement;
            setPopoverPosition(calculatePopoverPosition(linkSettingsPopoverAnchorRef));
        }
        setIsLinkSettingsPopoverOpen(true);
    }

    if (value === 'unselect_assets') {
      setSpaceAssetsPicked([]);
      setShowSelectedAssets(false);
      if (showSelectedAssets) {
        setSpaceAssetsSearchOptions({
          searchType: SPACE_ASSETS_SEARCH_TYPES.ALL,
          searchText: ''
        });
      }
    }
    return;
  };

  let bulkActionOptionsList = [
    {
      label: `${!showSelectedAssets
        ? `Show Only Selected Assets (${spaceAssetsPicked.length})`
        : `Show All (${totalCounts > 10000 ? '10000+' : totalCounts})`
        }`,
      testId: 'am-v2-space-assets-show-selected-assets',
      value: 'show_all_or_selected',
      icon: !showSelectedAssets ? 'v2-Eye' : '',
      showSelected: true,
      cb: () => handleBulkActionsCb('show_all_or_selected')
    },
    {
      label: `Unselect Assets (${spaceAssetsPicked.length})`,
      testId: 'am-v2-space-assets-unselect-assets',
      value: 'unselect_assets',
      cb: () => handleBulkActionsCb('unselect_assets')
    }
  ];

  if(isJsonRTE && !embedAsLink) {
    const newBulkActionOptionsList = bulkActionOptionsList.slice(0, 1);

    newBulkActionOptionsList.push({
        label: "Apply Embedding Settings",
        testId: 'am-v2-space-assets-apply-embedding-settings',
        value: 'apply_embedding_settings',
        cb: () => handleBulkActionsCb('apply_embedding_settings')
    });

    bulkActionOptionsList = [...newBulkActionOptionsList, ...bulkActionOptionsList.slice(1)];
  }

  if(embedAsLink) {
    const newBulkActionOptionsList = bulkActionOptionsList.slice(0, 1);

    newBulkActionOptionsList.push({
        label: "Apply Link Settings",
        testId: "am-v2-space-assets-apply-link-settings",
        value: "apply_link_settings",
        cb: () => handleBulkActionsCb("apply_link_settings")
    });

    bulkActionOptionsList = [...newBulkActionOptionsList, ...bulkActionOptionsList.slice(1)];
  }

  useEffect(() => {
    const countToShow = isAPIError ? 0 : showSelectedAssets ? spaceAssetsPicked.length : totalCounts;
    if (isFolderPickerMode) {
      setMessageComponent(
        <p className="space-assets-modal-info-message">
          Showing <b>{countToShow > 10000 ? '10000+' : countToShow}</b> folders
        </p>
      );
    } else if (isSuggestions) {
      setMessageComponent(
        <p className="space-assets-modal-info-message">
          Showing <b>{countToShow > 10000 ? '10000+' : countToShow}</b> records
        </p>
      );
    } else {
      setMessageComponent(
        <p className="space-assets-modal-info-message">
          Showing <b>{countToShow > 10000 ? '10000+' : countToShow}</b> records | Pro Tip: Press ⌘H to toggle filters,
          search, and details.
        </p>
      );
    }
  }, [totalCounts, showSelectedAssets, spaceAssetsPicked, setMessageComponent, isAPIError, isFolderPickerMode, isSuggestions]);

  // if spaceAssetsPicked is empty and showSelectedAssets is true, reset the state back to ALL
  useEffect(() => {
    if (spaceAssetsPicked.length === 0 && showSelectedAssets) {
      setSpaceAssetsPicked([]);
      setShowSelectedAssets(false);
      setSpaceAssetsSearchOptions({
        searchType: SPACE_ASSETS_SEARCH_TYPES.ALL,
        searchText: ''
      });
    }
  }, [spaceAssetsPicked]);

  const moveTargetSearchDropDown = [
    {
      label: 'Title (Search within title only)',
      testId: 'am-v2-space-assets-search-in-title',
      value: SPACE_ASSETS_SEARCH_TYPES.TITLE,
      disable: false
    }
  ];

  return (
    <div
      className={`SpaceAssetsModalBody${shouldShowInfoBanner ? ' SpaceAssetsModalBody--has-upload-info' : ''}`}
      ref={modalBodyRef}
    >
      {/* Search, Sorting & View Selector */}

      <div className="SpaceAssetsModalBody__action-panel">
        <div className="SpaceAssetsModalBody__navigation">
          <SpaceAssetsModalBreadcrumb
            breadcrumbItems={breadcrumbPath}
            shouldDisabled={isSuggestions || showSelectedAssets}
          />
        </div>
        {isMoveTarget && (
        <div className="SpaceAssetsModalHeader__move-info" data-testid="cs-bulk-move-info-section">
          <p className="SpaceAssetsModalHeader__move-subtitle">
          Move assets to a folder within this space, or create a new one.
          </p>
          <Info
            version='v2'
            type='light'
            content="Moving assets also moves all its localized and default versions. Users without access to the destination folder will lose access to the assets." />
        </div>
        )}
        {shouldShowInfoBanner && (
          <div
            className="SpaceAssetsModalHeader__upload-info"
            data-testid="cs-upload-target-info-banner"
          >
            <Info
              version='v2'
              type='light'
              dismissable
              onCancelClick={() => setIsInfoBannerVisible(false)}
              content={infoBanner}
            />
          </div>
        )}
        <div className="SpaceAssetsModalBody__filters-section">
          <div className="SpaceAssetsModalBody__search-sort-view">
            <SpaceAssetsModalSearch
              searchFor={'am-v2-space-assets-modal'}
              searchDropDownList={isFolderPickerMode ? moveTargetSearchDropDown : searchDropDownList}
              searchTypes={SPACE_ASSETS_SEARCH_TYPES}
              spaceAssetsSearchOptions={spaceAssetsSearchOptions}
              setSpaceAssetsSearchOptions={setSpaceAssetsSearchOptions}
              shouldDisabled={isSuggestions || showSelectedAssets}
              setIsSuggestions={setIsSuggestions}
              shouldClearSearchFilters={shouldClearSearchFilters}
              setShouldClearSearchFilters={setShouldClearSearchFilters}
              setShowSelectedAssets={setShowSelectedAssets}
              isResponsive={isResponsive}
              withDropdown={!isFolderPickerMode}
              placeholder={isFolderPickerMode ? 'Search folders...' : undefined}
            />
            <div
              className={`flex-v-center SpaceAssetsModalSortView ${isResponsive ? 'SpaceAssetsModalSortView--resp_screen' : ''
                }`}>
              {(effectiveViewType === SPACE_ASSETS_VIEW_TYPE.THUMBNAIL || effectiveViewType === SPACE_ASSETS_VIEW_TYPE.GALLERY) && (
                <SpaceAssetsModalSort
                  sortByData={sortByData}
                  setSortByData={setSortByData}
                  sortOptionsList={sortOptionsList}
                  dropDownLabel={`Sort by ${!isResponsive ? ':' : ''}`}
                  sortFor="am-v2-space-assets-thumbnail"
                  shouldDisabled={shouldDisabledActionPanel || showSelectedAssets}
                  isResponsive={isResponsive}
                />
              )}
              <Dropdown
                testId="cs-am-v2-space-assets-view-selector"
                className={`SpaceAssetsViewSelector ${isResponsive ? 'SpaceAssetsViewSelector--resp_screen' : ''
                  }`}
                version="v2"
                type="click"
                dropDownPosition="left"
                withArrow={true}
                closeAfterSelect={true}
                canCloseOnClickOutside={true}
                highlightActive={true}
                viewAs="label"
                list={viewOptionList}
                isDisabled={shouldDisabledActionPanel}>
                <Icon icon={getIconNameForViewType(viewType)} version="v2" size="medium" />
              </Dropdown>
            </div>
          </div>
          {/* Filters - hidden in folder picker modes */}
          {!isFolderPickerMode && (
            <div
              className={`SpaceAssetsModalBody__filters ${isResponsive ? 'SpaceAssetsModalBody__filters--resp_screen' : ''
                }`}>
              <SpaceAssetsModalFilter
                {...props}
                viewType={viewType}
                localeInfoBannerVisible={!localeInfoBannerDismissed}
                shouldDisabled={showSelectedAssets}
                isResponsive={isResponsive}
                screenVariant={screenVariant}
                appliedFilterCount={appliedFilterCount}
                setAppliedFilterCount={setAppliedFilterCount}
                ai_suggestions={ai_suggestions}
                setAiSuggestions={setAiSuggestions}
                linkedSpaces={spacesList || []}
              >
              </SpaceAssetsModalFilter>
            </div>
          )}
        </div>
      </div>
      {!localeInfoBannerDismissed && (
        <div className="SpaceAssetsModalBody__locale-info-banner" data-test-id="cs-asset-picker-locale-info-banner">
          <div className="SpaceAssetsModalBody__locale-info-banner__main">
            <span className="SpaceAssetsModalBody__locale-info-banner__icon">
              <Icon icon="InformationCircle" version="v2" size="medium" />
            </span>
            <span className="SpaceAssetsModalBody__locale-info-banner__message">
              As stacks and spaces can have its own locale hierarchy, while search for assets from CMS asset listing page and CMS asset picker, make sure that the linked spaces (and the corresponding workspace) have the corresponding locales and the assets localized in that locale
            </span>
          </div>
          <span
            className="SpaceAssetsModalBody__locale-info-banner__close"
            onClick={() => setLocaleInfoBannerDismissed(true)}
            data-test-id="cs-asset-picker-locale-info-banner-close"
          >
            <Icon icon="CloseNoborder" version="v2" size="medium" />
          </span>
        </div>
      )}
      {/* Space Assets Listing */}
      <div className="SpaceAssetsModalBody__items">
        {/* Bulk Actions Panel - hidden in folder picker modes */}
        {!isFolderPickerMode && (spaceAssetsPicked.length > 0 || totalCounts) ? (
          <BulkActionPanel
            bulkActionList={bulkActionOptionsList}
            selectedData={[]}
            updatedParentWidth={modalBulkActionPanelWidth}
            testId="am-v2-space-assets-bulk-action-panel"
          />
        ) : null}
        {/* Popovers - hidden in folder picker modes */}
        {!isFolderPickerMode && (
          <>
            <Popover
                isOpen={isEmbeddingPopoverOpen}
                onClose={() => setIsEmbeddingPopoverOpen(false)}
                anchorRef={embeddingPopoverAnchorRef}
                boundaryRef={null}
                popoverWidth={300}
                dismissOnClickOutside={false}
                dismissOnEscapeKey={false}
                testId="am-v2-embedding-settings-popover"
            >
                <div className="EmbeddingSettingsPopover__container">
                    <div className="EmbeddingSettingsPopover__header">
                        <Heading tagName='h6' text="Embedding Type" className="EmbeddingSettingsPopover__header-title" />
                        <Button
                            onClick={() => setIsEmbeddingPopoverOpen(false)}
                            onlyIcon={true}
                            icon={"v2-CloseNoborder"}
                            buttonType="tertiary"
                            size="small"
                            version="v2"
                            testId="am-v2-space-assets-embedding-settings-popover-close"
                            className="EmbeddingSettingsPopover__header-close-button"
                        />
                    </div>
                    <div className="EmbeddingSettingsPopover__content">
                        <Paragraph
                            text="Select an embedding type for your RTE based on how you want to insert content. Choose Block to add standalone elements (like images or entries). Choose Inline to embed elements directly within text."
                            tagName="p"
                            variant="p3"
                        />
                        <div className="EmbeddingSettingsPopover__content-radio-buttons">
                            <div className="EmbeddingSettingsPopover__content-radio-button">
                                <Radio
                                    name="embed-type"
                                    value="Block"
                                    checked={!isInline}
                                    text={
                                        <div className={'asset-modal-radio-wrapper' + (!isInline ? ' asset-modal-radio-active' : '')} >
                                        <div>Block</div>
                                        </div>
                                    }
                                    onClick={() => setIsInline(false)}
                                    testId={"asset-embed-block"}
                                    disabled={!spaceAssetsPicked?.length}
                                />
                            </div>
                            <div className="EmbeddingSettingsPopover__content-radio-button">
                                <Radio
                                    name="embed-type"
                                    value="Inline"
                                    checked={isInline}
                                    text={
                                        <div className={'asset-modal-radio-wrapper' + (isInline ? ' asset-modal-radio-active' : '')}>
                                        <div>Inline</div>
                                        </div>
                                    }
                                    onClick={() => setIsInline(true)}
                                    testId={"asset-embed-inline"}
                                    disabled={!spaceAssetsPicked?.length}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Popover>
            <Popover
                isOpen={isLinkSettingsPopoverOpen}
                onClose={() => setIsLinkSettingsPopoverOpen(false)}
                anchorRef={linkSettingsPopoverAnchorRef}
                boundaryRef={null}
                popoverWidth={300}
                dismissOnClickOutside={false}
                dismissOnEscapeKey={false}
                testId="am-v2-link-settings-popover"
            >
                <div className="LinkSettingsPopover__container">
                    <div className="LinkSettingsPopover__header">
                        <Heading tagName="h6" text="Link Settings" className="LinkSettingsPopover__header-title" />
                        <Button
                            onClick={() => setIsLinkSettingsPopoverOpen(false)}
                            onlyIcon={true}
                            icon="v2-CloseNoborder"
                            buttonType="tertiary"
                            size="small"
                            version="v2"
                            testId="am-v2-space-assets-link-settings-popover-close"
                            className="LinkSettingsPopover__header-close-button"
                        />
                    </div>
                    <div className="LinkSettingsPopover__content">
                        <Paragraph
                            text="Set the display text, title, and link behavior for your selected asset."
                            tagName="p"
                            variant="p3"
                        />
                        <div className="LinkSettingsPopover__content-fields">
                            <Field>
                                <FieldLabel version="v2" htmlFor="displayText">Display Text</FieldLabel>
                                <TextInput
                                    version="v2"
                                    name="displayText"
                                    value={linkSettings?.displayText || ""}
                                    onChange={(e) => {
                                        setLinkSettings({
                                            ...linkSettings,
                                            displayText: e.target.value
                                        });
                                    }}
                                    type="text"
                                    placeholder="Enter link text"
                                    autoComplete="off"
                                    testId="am-v2-asset-link-display-text"
                                    disabled={!spaceAssetsPicked?.length}
                                />
                            </Field>
                            <Field>
                                <FieldLabel 
                                    version="v2" 
                                    htmlFor="title"
                                >
                                    Title
                                </FieldLabel>
                                <TextInput
                                    version="v2"
                                    name="title"
                                    value={linkSettings?.title || ""}
                                    onChange={(e) => {
                                        setLinkSettings({
                                            ...linkSettings,
                                            title: e.target.value
                                        });
                                    }}
                                    type="text"
                                    placeholder="Enter link title"
                                    autoComplete="off"
                                    testId="am-v2-asset-link-title"
                                    disabled={!spaceAssetsPicked?.length}
                                />
                            </Field>
                            <div className="LinkSettingsPopover__toggle-wrapper">
                                <ToggleSwitch
                                    label="Open in a new tab"
                                    labelPosition="left"
                                    name="openInNewTab"
                                    checked={linkSettings?.openInNewTab || false}
                                    onChange={(e) => {
                                        setLinkSettings({
                                            ...linkSettings,
                                            openInNewTab: e.target.checked
                                        });
                                    }}
                                    testId="am-v2-asset-link-new-tab"
                                    disabled={!spaceAssetsPicked?.length}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Popover>
          </>
        )}
        <RenderViewComponent
          {...props}
          setMessageComponent={setMessageComponent}
          handleViewTypeChange={handleViewTypeChange}
          sortByData={sortByData}
          spaceAssetsSearchOptions={spaceAssetsSearchOptions}
          breadcrumbClickHandler={breadcrumbClickHandler}
          breadcrumbPath={breadcrumbPath}
          setBreadcrumbPath={setBreadcrumbPath}
          selectedFolder={selectedFolder}
          setSelectedFolder={setSelectedFolder}
          setTotalAssestsSelectedValue={setTotalAssestsSelectedValue}
          isMultiple={isFolderPickerMode ? false : field?.multiple}
          maxInstance={field?.max_instance}
          setShouldClearSearchFilters={setShouldClearSearchFilters}
          handleGoBack={handleGoBack}
          excludeUids={excludeUids}
          isAPIError={isAPIError}
          setIsAPIError={setIsAPIError}
          ai_suggestions={ai_suggestions}
          isSuggestions={isSuggestions}
          setShouldDisabledActionPanel={setShouldDisabledActionPanel}
          totalCounts={totalCounts}
          setTotalCounts={setTotalCounts}
          showSelectedAssets={showSelectedAssets}
          isResponsive={isResponsive}
          appliedFilterCount={appliedFilterCount}
          linkedWorkspaces={linkedWorkspaces}
          spacesWithTitles={spacesWithTitles}
          fetchSpaceAssets={fetchSpaceAssets}
          validationFilter={validationFilter}
          isMoveTarget={isFolderPickerMode}
          isUploadTarget={isUploadTarget}
          isNewlyCreatedFolder={isNewlyCreatedFolder}
          onSuggestionsFetchSettled={onSuggestionsFetchSettled}
        />
      </div>
    </div>
  );
};

export default SpaceAssetsSelectorModalBody;
